import { useEffect, useRef, useState } from "react";
import { Loader2, UploadCloud, Copy } from "lucide-react";
import { toast } from "sonner";
import { api, API, errMsg } from "@/lib/api";
import { METHOD, TOPUP_STATUS, TX_TYPE, PAY_NUMBER, egp, fmtDate, inputCls, cardCls, btnPrimary } from "@/lib/labels";
import { Field } from "./PasswordInput";

const EMPTY = { amount: "", method: "vodafone_cash", sender_phone: "" };

const TopupForm = ({ onDone }) => {
  const [f, setF] = useState(EMPTY);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    const amount = parseInt(f.amount, 10);
    if (!amount || amount < 1) return toast.error("اكتب المبلغ اللي حولته");
    if (!file) return toast.error("ارفع صورة إيصال التحويل");
    const fd = new FormData();
    fd.append("amount", amount);
    fd.append("method", f.method);
    fd.append("sender_phone", f.sender_phone.trim());
    fd.append("receipt", file);
    setBusy(true);
    try {
      await api.post("/customer/topups", fd);
      toast.success("طلب الشحن وصل، وهيتضاف الرصيد بعد المراجعة");
      setF(EMPTY);
      setFile(null);
      onDone();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className={`${cardCls} space-y-4 p-6`} data-testid="topup-form">
      <h3 className="font-display text-2xl text-white">اشحن محفظتك</h3>
      <div className="rounded-2xl border border-dashed border-[var(--km-cyan)]/50 bg-black/30 p-4 text-sm text-purple-100">
        <p>حوّل المبلغ على فودافون كاش أو إنستاباي على الرقم:</p>
        <button type="button" onClick={() => { navigator.clipboard?.writeText(PAY_NUMBER); toast.success("الرقم اتنسخ"); }}
          className="font-display mt-2 inline-flex items-center gap-2 text-3xl text-[var(--km-cyan)]" dir="ltr" data-testid="pay-number">
          {PAY_NUMBER} <Copy className="h-4 w-4" />
        </button>
        <p className="mt-2 text-purple-200/70">وبعدها املا البيانات وارفع صورة الإيصال. الرصيد بيتضاف بعد ما الإدارة تراجع التحويل.</p>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="المبلغ اللي حولته (ج.م)">
          <input type="text" inputMode="numeric" pattern="[0-9]*" autoComplete="off" dir="ltr" className={`${inputCls} text-right`} value={f.amount}
            onChange={(e) => setF((x) => ({ ...x, amount: e.target.value.replace(/[^0-9٠-٩]/g, "").replace(/[٠-٩]/g, (d) => String(d.charCodeAt(0) - 1632)) }))} required data-testid="topup-amount-input" />
        </Field>
        <Field label="طريقة الدفع">
          <div className="grid grid-cols-2 gap-2">
            {Object.entries(METHOD).map(([k, v]) => (
              <button type="button" key={k} onClick={() => setF((x) => ({ ...x, method: k }))} data-testid={`topup-method-${k}`}
                className={`font-head rounded-2xl border-2 px-3 py-3 text-sm font-bold ${f.method === k ? "border-[var(--km-pink)] bg-[var(--km-pink)]/20 text-white" : "border-purple-500/30 text-purple-200"}`}>{v}</button>
            ))}
          </div>
        </Field>
      </div>
      <Field label="رقم الموبايل اللي حولت منه">
        <input type="tel" dir="ltr" className={`${inputCls} text-right`} value={f.sender_phone} onChange={set("sender_phone")} required data-testid="topup-sender-input" />
      </Field>
      <div>
        <span className="font-head mb-1.5 block text-sm font-bold text-white">صورة إيصال التحويل</span>
        <button type="button" onClick={() => ref.current?.click()} data-testid="topup-receipt-btn"
          className="flex w-full items-center gap-3 rounded-2xl border-2 border-dashed border-purple-500/40 bg-black/30 p-4 text-right text-purple-100 hover:border-[var(--km-pink)]">
          <UploadCloud className="h-6 w-6 shrink-0 text-[var(--km-pink)]" />
          <span className="truncate" data-testid="topup-receipt-name">{file ? file.name : "اختار صورة (PNG, JPG, WEBP) لحد 10 ميجا"}</span>
        </button>
        <input ref={ref} type="file" accept=".png,.jpg,.jpeg,.webp" className="hidden" onChange={(e) => setFile(e.target.files[0] || null)} data-testid="topup-receipt-input" />
      </div>
      <button type="submit" disabled={busy} className={`${btnPrimary} w-full`} data-testid="topup-submit-btn">
        {busy && <Loader2 className="h-4 w-4 animate-spin" />} ابعت طلب الشحن
      </button>
    </form>
  );
};

const statusCls = { pending: "bg-yellow-500/20 text-yellow-200", approved: "bg-green-500/20 text-green-200", rejected: "bg-red-500/20 text-red-200" };

export const WalletPanel = ({ balance, tick, onChange }) => {
  const [topups, setTopups] = useState([]);
  const [txs, setTxs] = useState([]);
  const load = () => {
    api.get("/customer/topups").then((r) => setTopups(r.data)).catch(() => {});
    api.get("/customer/transactions").then((r) => setTxs(r.data)).catch(() => {});
  };
  useEffect(() => {
    load();
  }, [tick]);

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1.1fr_1fr] [&>*]:min-w-0" data-testid="wallet-panel">
      <div className="space-y-6">
        <div className={`${cardCls} bg-[var(--km-purple)] p-6`}>
          <p className="font-head text-white/80">رصيدك الحالي</p>
          <p className="font-display text-5xl text-white" data-testid="wallet-panel-balance">{egp(balance)}</p>
        </div>
        <TopupForm onDone={() => { load(); onChange(); }} />
      </div>
      <div className="space-y-6">
        <section className={`${cardCls} p-5`}>
          <h3 className="font-head mb-3 text-lg font-bold text-white">طلبات الشحن</h3>
          {topups.length === 0 ? <p className="text-sm text-purple-300/70" data-testid="topups-empty">مفيش طلبات شحن لسه</p> : (
            <ul className="space-y-2" data-testid="topups-list">
              {topups.map((t, i) => (
                <li key={t.id} className="flex items-center justify-between gap-2 rounded-2xl bg-black/30 p-3" data-testid={`topup-item-${i}`}>
                  <span>
                    <span className="font-head block font-bold text-white">{egp(t.amount)} · {METHOD[t.method]}</span>
                    <span className="block text-xs text-purple-300/60">{fmtDate(t.created_at)}</span>
                    {t.reject_reason && <span className="block text-xs text-red-300">{t.reject_reason}</span>}
                  </span>
                  <span className="flex items-center gap-2">
                    <a href={`${API}/customer/topups/${t.id}/receipt`} target="_blank" rel="noopener noreferrer" className="text-xs text-[var(--km-lilac)] underline">الإيصال</a>
                    <span className={`rounded-full px-3 py-1 text-xs font-bold ${statusCls[t.status]}`} data-testid={`topup-status-${i}`}>{TOPUP_STATUS[t.status]}</span>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>
        <section className={`${cardCls} p-5`}>
          <h3 className="font-head mb-3 text-lg font-bold text-white">سجل المعاملات</h3>
          {txs.length === 0 ? <p className="text-sm text-purple-300/70" data-testid="transactions-empty">مفيش معاملات لسه</p> : (
            <ul className="space-y-2" data-testid="transactions-list">
              {txs.map((t, i) => (
                <li key={t.id} className="flex items-center justify-between gap-2 rounded-2xl bg-black/30 p-3" data-testid={`transaction-item-${i}`}>
                  <span>
                    <span className="font-head block text-sm font-bold text-white">{TX_TYPE[t.type] || t.type}</span>
                    <span className="block text-xs text-purple-300/70">{t.note}</span>
                    <span className="block text-[11px] text-purple-300/50">{fmtDate(t.created_at)}</span>
                  </span>
                  <span className={`font-head shrink-0 font-bold ${t.amount < 0 ? "text-red-300" : "text-green-300"}`} dir="ltr">{t.amount > 0 ? "+" : ""}{Number(t.amount).toLocaleString("ar-EG")}</span>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
};
